/**
 * La firma depositata di un utente.
 *
 * Chi emette offerte finora stampava, firmava e scansionava. Depositando qui
 * l'immagine della propria firma, il documento esce già firmato e al cliente
 * resta da compilare solo la riga dell'accettazione.
 *
 * L'immagine sta nel gestionale, non in un file sul computer di qualcuno: una
 * firma che gira come allegato prima o poi finisce dove non deve.
 */
import { useEffect, useRef, useState } from 'react';
import { authApi } from '../api';
import Modal from './Modal';

export default function FirmaUtente({ utente, onCambiato }) {
  const [aperto, setAperto] = useState(false);
  const [lavorando, setLavorando] = useState(false);
  const [errore, setErrore] = useState('');
  // L'immagine è protetta: si scarica con il token e si mostra da un URL
  // temporaneo, che si rigenera a ogni salvataggio.
  const [versione, setVersione] = useState(0);
  const [anteprima, setAnteprima] = useState(null);
  const input = useRef(null);

  useEffect(() => {
    if (!aperto || !utente.ha_firma) { setAnteprima(null); return; }
    let vivo = true, url = null;
    authApi.firmaBlob(utente.id)
      .then(b => { if (!vivo) return; url = URL.createObjectURL(b); setAnteprima(url); })
      .catch(() => {});
    return () => { vivo = false; if (url) URL.revokeObjectURL(url); };
  }, [aperto, versione, utente.id, utente.ha_firma]);

  const carica = async (file) => {
    if (!file) return;
    setLavorando(true); setErrore('');
    try {
      await authApi.caricaFirma(utente.id, file);
      setVersione(v => v + 1);
      onCambiato?.();
    } catch (e) { setErrore(String(e?.error || e)); } finally { setLavorando(false); }
  };

  const togli = async () => {
    if (!confirm(`Togliere la firma depositata di ${utente.nome}? I documenti torneranno con la riga da firmare a mano.`)) return;
    setLavorando(true); setErrore('');
    try {
      await authApi.eliminaFirma(utente.id);
      setVersione(v => v + 1);
      onCambiato?.();
    } catch (e) { setErrore(String(e?.error || e)); } finally { setLavorando(false); }
  };

  return (
    <>
      <button type="button" className="btn-secondary btn-sm" onClick={() => setAperto(true)}
              title="Firma che compare nelle offerte emesse da questo utente">
        {utente.ha_firma ? '✓ Firma' : 'Firma'}
      </button>

      {aperto && (
        <Modal title={`Firma di ${utente.nome} ${utente.cognome || ''}`} onClose={() => setAperto(false)}>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-gray-500">
              Compare in fondo alle offerte emesse da questo utente, al posto della riga
              da firmare a mano. La riga per l'accettazione del cliente resta.
            </p>

            {utente.ha_firma ? (
              <div className="border rounded-lg p-3 bg-white min-h-[6rem] flex items-center justify-center">
                {anteprima
                  ? <img src={anteprima} alt="Firma depositata" className="max-h-24" />
                  : <span className="text-xs text-gray-400">Carico l'anteprima…</span>}
              </div>
            ) : (
              <div className="border border-dashed rounded-lg p-6 text-center text-xs text-gray-400">
                Nessuna firma depositata: i documenti escono con la riga da firmare a mano.
              </div>
            )}

            {errore && (
              <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-xs text-red-700">{errore}</div>
            )}

            <div className="text-xs text-gray-500">
              Immagine PNG o JPG, fino a 2 MB. Meglio una firma su fondo bianco, ritagliata
              stretta: nel documento viene scalata ad altezza fissa.
            </div>

            <input ref={input} type="file" accept="image/png,image/jpeg" className="hidden"
                   onChange={e => { carica(e.target.files?.[0]); e.target.value = ''; }} />
            <div className="flex gap-2">
              <button className="btn-primary" disabled={lavorando} onClick={() => input.current?.click()}>
                {lavorando ? 'Carico…' : utente.ha_firma ? 'Sostituisci' : 'Carica la firma'}
              </button>
              {utente.ha_firma && (
                <button className="btn-secondary" disabled={lavorando} onClick={togli}>Togli</button>
              )}
              <button className="btn-secondary ml-auto" onClick={() => setAperto(false)}>Chiudi</button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
